import { createSelector } from 'reselect';
import { BaggageType, CheckinOrderStatus, GdsServiceVipSubType, OrderAdditionalServiceGdsServiceServiceType, OrderStatus, TravellerFieldEnum } from '@websky/graphql';
import { getHasRequestedServices, getSeatMapUniqueAvailableSegments, getTotalPrice as getTotalPriceUtil, getTravellerName, getUserValue } from '../../../../utils';
import { convertBaggageObject, convertMealObject } from '../../../../Checkout/types';
import { checkoutSelectedServicesStateFactory } from '../../../../Checkout/utils';
import { getOrderServiceType } from './getOrderServiceType';
import { convertIncludedBaggageToSelected } from './convertIncludedBaggageToSelected';
import { getExistUserType } from '@shared/lib/passenger';
function isCheckoutOrder(order) {
    return order.flight !== undefined;
}
function isIncludeGdsServiceType(types) {
    return function (service) {
        return types.includes(service.type);
    };
}
function isIncludeBaggageType(types) {
    return function (baggage) {
        if (!types.length) {
            return false;
        }
        return types.includes(baggage.baggageType);
    };
}
function getBaggageServiceByType(types) {
    return function (baggage) {
        if (!types.length) {
            return false;
        }
        return types.includes(baggage.type);
    };
}
function filterBaggageServicesByTypes(types) {
    return function (services) {
        return services.filter(getBaggageServiceByType(types));
    };
}
function isIncludeVipSubType(subTypes) {
    return function (vipService) {
        if (!subTypes.length) {
            return false;
        }
        return subTypes.includes(vipService.subType);
    };
}
function getPriceToPayPart(part) {
    return function (priceToPay, currency) {
        const priceByPart = priceToPay[part];
        if (!priceByPart) {
            return { amount: 0, currency };
        }
        return Object.assign(Object.assign({}, priceByPart), { currency });
    };
}
export function createCommonOrderSelectors(getOrder) {
    // basic
    const getOrderId = createSelector(getOrder, order => order.id);
    const getStatus = createSelector(getOrder, order => order.status);
    const getFlight = createSelector(getOrder, (order) => {
        if (isCheckoutOrder(order)) {
            return order.flight;
        }
        return {
            seatMap: order.seatMap,
            segments: order.segments.map(segment => ({ segment: Object.assign(Object.assign({}, segment), { messages: [] }) })),
            segmentGroups: [],
            fares: []
        };
    });
    const getSegments = createSelector(getOrder, (order) => {
        if (isCheckoutOrder(order)) {
            return order.flight.segments.map(({ segment }) => segment);
        }
        return order.segments.map(segment => (Object.assign(Object.assign({}, segment), { messages: [] })));
    });
    const getTravellers = createSelector(getOrder, order => {
        if (isCheckoutOrder(order)) {
            return order.travellers;
        }
        return order.travellers.map(traveller => (Object.assign(Object.assign({}, traveller), { fares: [], tickets: [], needOthsSsr: false })));
    });
    const getTravellersWithVisa = createSelector(getOrder, order => {
        if (isCheckoutOrder(order)) {
            return [];
        }
        return order.travellers.filter(traveller => { var _a; return traveller.visaRequired && ((_a = traveller.visaValues) === null || _a === void 0 ? void 0 : _a.length); });
    });
    const getTravellersNames = createSelector(getTravellers, travellers => {
        return travellers.map(traveller => ({ id: traveller.id, name: getTravellerName(traveller) }));
    });
    const getSeatMap = createSelector(getOrder, order => isCheckoutOrder(order) ? order.flight.seatMap : order.seatMap);
    const getSeatMapAvailableSegments = createSelector(getSeatMap, seatMap => {
        return getSeatMapUniqueAvailableSegments(seatMap.segments);
    });
    const getMessages = createSelector(getOrder, order => order.messages);
    const getSsrData = createSelector(getOrder, order => (isCheckoutOrder(order) ? order.ssrData : undefined));
    const getFfpInfo = createSelector(getOrder, order => (isCheckoutOrder(order) ? order.ffpInfo : undefined));
    const getFfpBonusForFlight = createSelector(getFfpInfo, ffpInfo => ffpInfo === null || ffpInfo === void 0 ? void 0 : ffpInfo.bonusForFlight);
    const getFfpBonusByTravellers = createSelector(getFfpInfo, getTravellers, (ffpInfo, travellers) => {
        var _a;
        const travellersWithBonus = {};
        const travellerCardFfpInfoMap = new Map(((_a = ffpInfo.bonusByCard) === null || _a === void 0 ? void 0 : _a.filter(b => b.cardNumber).map(b => [b.cardNumber, b])) || []);
        for (const traveller of travellers) {
            if (!getExistUserType(traveller, TravellerFieldEnum.LoyaltyNumber)) {
                continue;
            }
            const loyaltyNumber = getUserValue(traveller, TravellerFieldEnum.LoyaltyNumber);
            const cardFfpInfo = travellerCardFfpInfoMap.get(loyaltyNumber);
            travellersWithBonus[traveller.id] = {
                cardNumber: cardFfpInfo === null || cardFfpInfo === void 0 ? void 0 : cardFfpInfo.cardNumber,
                bonusForFlight: cardFfpInfo === null || cardFfpInfo === void 0 ? void 0 : cardFfpInfo.bonusForFlight
            };
        }
        return travellersWithBonus;
    });
    // price
    const getPrice = createSelector(getOrder, order => order.price);
    const getPriceToPay = createSelector(getOrder, order => order.priceToPay);
    const getPriceBreakdown = createSelector(getOrder, order => order.priceBreakdown);
    const getCurrency = createSelector(getPriceToPay, priceToPay => { var _a; return (_a = priceToPay.total) === null || _a === void 0 ? void 0 : _a.currency; });
    const getMilesPrice = createSelector(getPrice, price => price.totalInMiles);
    const getSeatPrice = createSelector(getPriceToPay, getCurrency, getPriceToPayPart('seats'));
    const getBaggagePrice = createSelector(getPriceToPay, getCurrency, getPriceToPayPart('baggage'));
    const getMealPrice = createSelector(getPriceToPay, getCurrency, getPriceToPayPart('meals'));
    const getInsurancePrice = createSelector(getPriceToPay, getCurrency, getPriceToPayPart('insurance'));
    const getAeroexpressPrice = createSelector(getPriceToPay, getCurrency, getPriceToPayPart('aeroexpress'));
    const getExtrasPrice = createSelector(getInsurancePrice, getAeroexpressPrice, (insurancePrice, aeroexpressPrice) => {
        return getTotalPriceUtil([insurancePrice, aeroexpressPrice], price => price);
    });
    const getBusinessLoungePrice = createSelector(getPriceToPay, getCurrency, getPriceToPayPart('businessLounge'));
    const getVipPrice = createSelector(getPriceToPay, getCurrency, getPriceToPayPart('vip'));
    const getAirTicketPrice = createSelector(getPriceToPay, getCurrency, getPriceToPayPart('airticket'));
    const getChargePrice = createSelector(getPriceToPay, getCurrency, getPriceToPayPart('charge'));
    const getTotalPrice = createSelector(getPriceToPay, getCurrency, getPriceToPayPart('total'));
    // services
    const getSelectedServices = createSelector(getOrder, checkoutSelectedServicesStateFactory);
    const getAdditionalServices = createSelector(getOrder, order => order.additionalServices);
    const getGdsServices = createSelector(getAdditionalServices, additionalServices => { var _a, _b; return (_b = (_a = additionalServices.gdsServices) === null || _a === void 0 ? void 0 : _a.services) !== null && _b !== void 0 ? _b : []; });
    const getIncludedServices = createSelector(getTravellers, travellers => {
        var _a, _b;
        const includedServices = {};
        for (const traveller of travellers) {
            const travellerId = traveller.id;
            const travellerIncludedServices = (_b = (_a = traveller.services) === null || _a === void 0 ? void 0 : _a.brandIncludedServices) === null || _b === void 0 ? void 0 : _b.services;
            if (!(travellerIncludedServices === null || travellerIncludedServices === void 0 ? void 0 : travellerIncludedServices.length)) {
                continue;
            }
            for (const includedService of travellerIncludedServices) {
                const serviceType = getOrderServiceType(includedService.service);
                if (!['Baggage', 'BaggagePet', 'BaggageDelivery', 'Meal'].includes(serviceType)) {
                    continue;
                }
                if (!includedServices[serviceType]) {
                    includedServices[serviceType] = {};
                }
                if (!includedServices[serviceType][travellerId]) {
                    includedServices[serviceType][travellerId] = [];
                }
                includedServices[serviceType][travellerId].push(includedService);
            }
        }
        return includedServices;
    });
    const getSeatServices = createSelector(getGdsServices, gdsServices => {
        return gdsServices.filter(isIncludeGdsServiceType([OrderAdditionalServiceGdsServiceServiceType.Seat]));
    });
    const getBaggageServices = createSelector(getGdsServices, gdsServices => {
        return gdsServices.filter(isIncludeGdsServiceType([OrderAdditionalServiceGdsServiceServiceType.Baggage]));
    });
    const getBaggagePetServices = createSelector(getBaggageServices, baggageServices => {
        return baggageServices.filter(isIncludeBaggageType([BaggageType.PetInCabin, BaggageType.PetInHold, BaggageType.PetInHandCarrier]));
    });
    const getConvertedBaggageServices = createSelector(getBaggageServices, baggageServices => {
        return baggageServices.map(convertBaggageObject);
    });
    const getConvertedBaggageIncludedServices = createSelector(getIncludedServices, includedServices => {
        const includedBaggage = [];
        const includedBaggageServices = includedServices['Baggage'];
        if (!includedBaggageServices) {
            return [];
        }
        for (const [travellerId, includedBaggageByTraveller] of Object.entries(includedBaggageServices)) {
            for (const included of Object.values(includedBaggageByTraveller)) {
                includedBaggage.push(convertIncludedBaggageToSelected(travellerId, included));
            }
        }
        return includedBaggage;
    });
    const getUpgradeBaggageServices = createSelector(getConvertedBaggageServices, filterBaggageServicesByTypes([BaggageType.HandLuggageExcess]));
    const getAvailableUpgradeBaggageServices = createSelector(getUpgradeBaggageServices, getTravellers, (upgradeBaggage, travellers) => {
        const travellersByIdMap = new Map(travellers.map(traveller => {
            var _a, _b;
            if (!((_b = (_a = traveller.services.gdsServices) === null || _a === void 0 ? void 0 : _a.services) === null || _b === void 0 ? void 0 : _b.length)) {
                return [traveller.id, null];
            }
            const travellerGdsServicesById = new Map(traveller.services.gdsServices.services.map(service => [
                service.serviceId,
                service.count > service.confirmedCount
            ]));
            return [traveller.id, travellerGdsServicesById];
        }));
        const isCanBeAddedOrRemoved = (item) => {
            const canBeRemoved = item.allowedPassengers.some(passengerId => { var _a; return (_a = travellersByIdMap.get(passengerId)) === null || _a === void 0 ? void 0 : _a.get(item.id); });
            return item.canBeAdded || canBeRemoved;
        };
        return upgradeBaggage.filter(item => {
            return item.type === BaggageType.HandLuggageExcess && isCanBeAddedOrRemoved(item);
        });
    });
    const getAdditionalBaggageServices = createSelector(getConvertedBaggageServices, filterBaggageServicesByTypes([BaggageType.CheckedBaggage]));
    const getSpecialBaggageServices = createSelector(getConvertedBaggageServices, filterBaggageServicesByTypes([
        BaggageType.SportingEquipment,
        BaggageType.PetInCabin,
        BaggageType.PetInHold,
        BaggageType.MusicalInstrument
    ]));
    const getOverweightBaggageServices = createSelector(getConvertedBaggageServices, filterBaggageServicesByTypes([BaggageType.BaggageExcess]));
    const getUpgradableBaggageServices = createSelector(getAvailableUpgradeBaggageServices, getAdditionalBaggageServices, getSpecialBaggageServices, getOverweightBaggageServices, (...baggage) => baggage.reduce((services, items) => [...services, ...items], []));
    const getMealServices = createSelector(getGdsServices, gdsServices => {
        return gdsServices.filter(isIncludeGdsServiceType([OrderAdditionalServiceGdsServiceServiceType.Meal]));
    });
    const getConvertedMealServices = createSelector(getMealServices, mealServices => {
        return mealServices.map(convertMealObject);
    });
    const getMealRecommendedServices = createSelector(getConvertedMealServices, getTravellers, (mealServices, travellers) => {
        const recommended = [];
        if (!mealServices.length) {
            return recommended;
        }
        const mealServiceMap = new Map(mealServices.map(service => [service.id, service]));
        travellers.forEach(traveller => {
            var _a, _b;
            (_b = (_a = traveller.preselectedServices) === null || _a === void 0 ? void 0 : _a.gdsServices) === null || _b === void 0 ? void 0 : _b.services.forEach(service => {
                const meal = mealServiceMap.get(service.serviceId);
                if (meal) {
                    recommended.push({
                        meal,
                        travellerId: traveller.id,
                        segmentIds: service.segmentIds,
                        count: service.count
                    });
                }
            });
        });
        return recommended;
    });
    const getVipServices = createSelector(getGdsServices, gdsServices => {
        return gdsServices.filter(isIncludeGdsServiceType([OrderAdditionalServiceGdsServiceServiceType.Vip]));
    });
    const getBaggageDeliveryServices = createSelector(getVipServices, vipServices => {
        return vipServices.filter(isIncludeVipSubType([GdsServiceVipSubType.BaggageDelivery]));
    });
    const getPriorityBoardingServices = createSelector(getVipServices, vipServices => {
        return vipServices.filter(isIncludeVipSubType([GdsServiceVipSubType.PriorityBoarding]));
    });
    const getCapsuleHotelServices = createSelector(getVipServices, vipServices => {
        return vipServices.filter(isIncludeVipSubType([GdsServiceVipSubType.CapsuleHotel]));
    });
    const getExtraSeatServices = createSelector(getVipServices, vipServices => {
        return vipServices.filter(service => service.rfisc === 'EXST' && service.subType === GdsServiceVipSubType.ExtraSeat);
    });
    const getBusinessLoungeServices = createSelector(getGdsServices, gdsServices => {
        return gdsServices.filter(service => {
            if (service.type === OrderAdditionalServiceGdsServiceServiceType.Vip) {
                const vipService = service;
                return (vipService.subType === GdsServiceVipSubType.BusinessLounge ||
                    vipService.subType === GdsServiceVipSubType.BusinessLoungeInternational);
            }
            return service.type === OrderAdditionalServiceGdsServiceServiceType.BusinessLounge;
        });
    });
    const hasSeatServices = createSelector(getSeatMap, getSeatServices, (seatMap, seatServices) => {
        var _a;
        return !!((_a = seatMap.segments) === null || _a === void 0 ? void 0 : _a.some(segment => segment.isAnySeatExistsAndFree)) || !!seatServices.length;
    });
    // special services
    const getEveryPortServices = createSelector(getAdditionalServices, additionalServices => additionalServices.nas);
    const getInsurances = createSelector(getAdditionalServices, additionalServices => additionalServices.insurances);
    const getTransfer = createSelector(getAdditionalServices, additionalServices => additionalServices.transfer);
    const getAeroexpress = createSelector(getTransfer, transfer => transfer === null || transfer === void 0 ? void 0 : transfer.aeroexpress);
    // flags
    const getIsOrderNew = createSelector(getStatus, status => status === OrderStatus.New || status === CheckinOrderStatus.New);
    const getIsOrderBooked = createSelector(getStatus, status => status === OrderStatus.Booked);
    const getIsOrderStarted = createSelector(getStatus, status => status === CheckinOrderStatus.Started);
    const getIsOrderConfirmed = createSelector(getStatus, status => status === OrderStatus.Confirmed || status === CheckinOrderStatus.Confirmed);
    const getIsOrderCancelled = createSelector(getStatus, status => status === OrderStatus.Cancelled);
    const getIsOrderExpired = createSelector(getStatus, status => status === CheckinOrderStatus.Expired);
    const getIsOrderProblematic = createSelector(getStatus, status => status === OrderStatus.Problematic);
    const getIsContactsInfoEmpty = createSelector(getOrder, order => order.customer.contactInfoIsEmpty);
    const getIsTravellersDataFilled = createSelector(getOrder, order => order.isTravellersDataFilled);
    const getIsBlockedForPayment = createSelector(getOrder, order => order.isBlockedForPayment);
    const getIsSkipSeatSelectionAllowed = createSelector(getOrder, order => !isCheckoutOrder(order) && order.isSkipSeatSelectionAllowed);
    const getIsFfpSupported = createSelector(getFfpInfo, ffpInfo => !!(ffpInfo === null || ffpInfo === void 0 ? void 0 : ffpInfo.ffpSupported));
    const getIsSeatMapUnavailable = createSelector(getSeatMap, getTravellers, (seatMap, travellers) => {
        var _a;
        const hasSegmentsWithZeroAvailableSeats = (_a = seatMap.segments) === null || _a === void 0 ? void 0 : _a.some(segment => !segment.availableSeatsCount);
        const allTravellersHaveNotSeats = travellers.every(traveller => { var _a, _b; return !((_b = (_a = traveller.services) === null || _a === void 0 ? void 0 : _a.seats) === null || _b === void 0 ? void 0 : _b.length); });
        return hasSegmentsWithZeroAvailableSeats && allTravellersHaveNotSeats;
    });
    const getIsAllTravellersHavePreSeating = createSelector(getOrder, order => !isCheckoutOrder(order) && order.allTravellersHavePreseating);
    const getIsConfirmedOrPreSeating = createSelector(getIsOrderConfirmed, getIsAllTravellersHavePreSeating, (isConfirmed, isAllTravellersHavePreSeating) => isConfirmed && isAllTravellersHavePreSeating);
    const getIsAvailableSeats = createSelector(getSeatMap, seatMap => {
        var _a;
        return (_a = seatMap.segments) === null || _a === void 0 ? void 0 : _a.some(segment => !!(segment === null || segment === void 0 ? void 0 : segment.availableSeatsCount));
    });
    const getIsInsuranceSeparateStep = createSelector(getInsurances, insurances => !!(insurances === null || insurances === void 0 ? void 0 : insurances.separateStep));
    const getHasRequestedOrderServices = createSelector(getOrder, getTravellers, getSsrData, (order, travellers, ssrData) => {
        const checkRequestedServices = isCheckoutOrder(order)
            ? order.status === OrderStatus.Booked || order.status === OrderStatus.Confirmed
            : order.status === CheckinOrderStatus.Started;
        return checkRequestedServices && getHasRequestedServices(travellers, ssrData);
    });
    return {
        // basic
        getOrder,
        getOrderId,
        getStatus,
        getFlight,
        getSegments,
        getTravellers,
        getTravellersWithVisa,
        getTravellersNames,
        getSeatMap,
        getSeatMapAvailableSegments,
        getMessages,
        getFfpInfo,
        getFfpBonusForFlight,
        getFfpBonusByTravellers,
        // price
        getPrice,
        getPriceToPay,
        getPriceBreakdown,
        getCurrency,
        getMilesPrice,
        getSeatPrice,
        getBaggagePrice,
        getMealPrice,
        getInsurancePrice,
        getAeroexpressPrice,
        getExtrasPrice,
        getBusinessLoungePrice,
        getVipPrice,
        getAirTicketPrice,
        getChargePrice,
        getTotalPrice,
        // services
        getSelectedServices,
        getAdditionalServices,
        getGdsServices,
        getSeatServices,
        getBaggageServices,
        getBaggagePetServices,
        getConvertedBaggageServices,
        getConvertedBaggageIncludedServices,
        getUpgradeBaggageServices,
        getAvailableUpgradeBaggageServices,
        getAdditionalBaggageServices,
        getSpecialBaggageServices,
        getOverweightBaggageServices,
        getUpgradableBaggageServices,
        getMealServices,
        getConvertedMealServices,
        getMealRecommendedServices,
        getVipServices,
        getBaggageDeliveryServices,
        getPriorityBoardingServices,
        getCapsuleHotelServices,
        getExtraSeatServices,
        getBusinessLoungeServices,
        hasSeatServices,
        // special services
        getEveryPortServices,
        getInsurances,
        getTransfer,
        getAeroexpress,
        // flags
        getIsOrderNew,
        getIsOrderBooked,
        getIsOrderStarted,
        getIsOrderConfirmed,
        getIsOrderCancelled,
        getIsOrderExpired,
        getIsOrderProblematic,
        getIsContactsInfoEmpty,
        getIsTravellersDataFilled,
        getIsBlockedForPayment,
        getIsSkipSeatSelectionAllowed,
        getIsFfpSupported,
        getIsSeatMapUnavailable,
        getIsAllTravellersHavePreSeating,
        getIsConfirmedOrPreSeating,
        getIsAvailableSeats,
        getIsInsuranceSeparateStep,
        getHasRequestedOrderServices
    };
}
